import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useReportsServiceDeleteReportEndpoint,
  useReportsServiceSearchReportsEndpoint,
  useReportsServiceSearchReportsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  ReportItemDto
} from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  RangePicker,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler, resetPagination } from 'src/utils'

import { ReportModal } from '../components'

export const ReportsAdministrationPage = () => {
  const { t, currentLang } = useLocales()

  const pagination = useTablePagination()

  const [search, setSearch] = useState('')
  const [categories, setCategories] = useState<string[]>([])
  const [createDateFrom, setCreateDateFrom] = useState<string>()
  const [createDateTo, setCreateDateTo] = useState<string>()

  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryClient = useQueryClient()

  const categoryClassifierId = useClassifierId(
    ClassifierType.REPORT_CATEGORY,
    ClassifierCategory.REPORTS
  )

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    searchTerm: search,
    categoryIds: categories,
    createDateFrom,
    createDateTo
  }

  const { data, isLoading } =
    useReportsServiceSearchReportsEndpoint(queryParams)

  const { mutate: deleteReport } = useReportsServiceDeleteReportEndpoint({
    onError: (error: ApiError) => formErrorHandler(error.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useReportsServiceSearchReportsEndpointKey]
      })
      message.success(t('reports_administration.report_deleted'))
    }
  })

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const getActionItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        onToggle()
      }
    },
    {
      key: 'divider',
      type: 'divider'
    },
    {
      key: 'delete',
      label: (
        <DeleteConfirm
          title={t('reports_administration.delete_report')}
          onConfirm={() => deleteReport({ id })}
        />
      ),
      danger: true
    }
  ]

  const columns: ColumnsType<ReportItemDto> = [
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: (_, { title }) => title?.[currentLang?.value]
    },
    {
      key: 'category',
      title: t('classifiers.category'),
      render: (_, { category }) => category?.translation?.[currentLang?.value]
    },
    {
      key: 'data source',
      title: t('common.type'),
      render: (_, { dataSource }) =>
        t(`reports_administration.type.${dataSource}`)
    },
    {
      key: 'actions',
      render: (record) => (
        <Flex justify="center">
          <TableActionButtons items={getActionItems(record.id)} />
        </Flex>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.reports_administration')}
      items={[{ title: t('menu.reports_administration') }]}
    >
      <Flex justify="space-between" gap={16}>
        <Flex gap={16}>
          <SearchBar onSearch={onSearch} />

          {categoryClassifierId && (
            <ClassifierInfiniteSelect
              mode="multiple"
              placeholder={t('classifiers.category')}
              id={categoryClassifierId}
              style={{ width: 300 }}
              maxTagCount={1}
              onChange={(value) => setCategories(value)}
            />
          )}

          <RangePicker
            resetPagination={() => resetPagination(pagination)}
            setDateFrom={setCreateDateFrom}
            setDateTo={setCreateDateTo}
          />
        </Flex>

        <Button type="primary" onClick={onToggle}>
          <PlusOutlined />
          {t('common.add')}
        </Button>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        dataSource={data?.data}
        loading={isLoading}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && <ReportModal onCancel={onCloseModal} id={currentId} />}
    </PageBreadcrumbs>
  )
}
